/**
 * Render assistant Markdown → sanitized HTML for v-html.
 *
 * The AI-marketer (and most LLM providers) reply in Markdown — `**bold**`,
 * headings, lists, `code`, links. We render it instead of showing the raw
 * syntax. Output is ALWAYS run through DOMPurify with a tight allowlist (no
 * img/script/iframe/style, only safe inline + block text tags, hrefs limited to
 * http(s)/mailto/tel/relative) so a malicious or malformed model response can't
 * inject active content.
 *
 * Use ONLY for assistant turns — never render user-typed text as Markdown/HTML.
 */
import { marked } from "marked";
import DOMPurify from "dompurify";

marked.setOptions({ breaks: true, gfm: true });

const ALLOWED_TAGS = [
  "p", "br", "strong", "em", "b", "i", "u", "del", "s", "code", "pre", "kbd",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote",
  "a", "hr", "table", "thead", "tbody", "tr", "th", "td", "span",
];
const ALLOWED_ATTR = ["href", "target", "rel", "class"];
// Only safe link schemes + relative (in-app routes the assistant cites).
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|tel:|\/)/i;

export function renderMarkdown(src: string): string {
  if (!src || typeof src !== "string") return "";
  const html = marked.parse(src, { async: false }) as string;
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP });
}

/**
 * Sanitize tenant-authored Telegram HTML for the admin preview (QA F21).
 *
 * The Telegram preview renders a template body authored by ANY in-tenant role
 * (operator+) and viewed by an admin/super_admin — different privilege levels —
 * so an unsanitized v-html let an operator run JS in an admin's session
 * (`<img src=x onerror=…>`). We allow only Telegram's own supported HTML subset
 * (https://core.telegram.org/bots/api#html-style); everything else — img, script,
 * style, and ALL on* event handlers — is stripped by DOMPurify. Output is still
 * fed to v-html, but now it can carry no active content.
 */
const TG_ALLOWED_TAGS = [
  "b", "strong", "i", "em", "u", "ins", "s", "strike", "del",
  "a", "code", "pre", "span", "tg-spoiler", "blockquote", "br",
];
const TG_ALLOWED_ATTR = ["href", "class"]; // NO on* handlers, NO src/style
export function sanitizeTelegramHtml(src: string): string {
  if (!src || typeof src !== "string") return "";
  return DOMPurify.sanitize(src, {
    ALLOWED_TAGS: TG_ALLOWED_TAGS,
    ALLOWED_ATTR: TG_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: ALLOWED_URI_REGEXP,
  });
}
